import ViewInArIcon from '@mui/icons-material/ViewInAr';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useCompanyId, useAreaId } from '../../contexts/CompaniesAreasContext';
import { useGetCompanyAreas } from '../../hooks/companies/list-company-areas';

function AreaSelect(): JSX.Element {
  const { t } = useTranslation();
  const { companyId, disabled } = useCompanyId();
  const { areaId, setAreaId, resetAreaId } = useAreaId();

  if (!companyId) {
    throw new Error('No CompanyId provided');
  }

  const { isLoading, isError, data } = useGetCompanyAreas(999, 1, { sortModel: [] });

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClickButton = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAreaChange = (id: string): void => {
    const selectedCompany = data?.data.find((company) => company.area_id === id);
    if (selectedCompany && id !== 'ALL') {
      setAreaId({
        id: selectedCompany.area_id,
        name: selectedCompany.name,
        coordinates: [
          {
            lat: selectedCompany.coordinates.latitude,
            lng: selectedCompany.coordinates.longitude,
          },
        ],
        timezone: selectedCompany.timezone,
      });
    } else if (id === 'ALL') {
      setAreaId({
        id: 'ALL',
        name: 'All',
        coordinates: [
          {
            lat: companyId ? companyId.coordinates[0].lat : 0,
            lng: companyId ? companyId.coordinates[0].lng : 0,
          },
        ],
        timezone: companyId ? companyId.timezone : '',
      });
    }
    handleClose();
  };

  const setDefaultAreaId = (): void => {
    if (!isLoading && !isError && data && data.data.length > 0) {
      if (data.data.length > 1) {
        setAreaId({
          id: 'ALL',
          name: 'All',
          coordinates: [
            {
              lat: companyId ? companyId.coordinates[0].lat : 0,
              lng: companyId ? companyId.coordinates[0].lng : 0,
            },
          ],
          timezone: companyId ? companyId.timezone : '',
        });
      } else {
        setAreaId({
          id: data.data[0].area_id,
          name: data.data[0].name,
          coordinates: [
            {
              lat: data.data[0].coordinates.latitude,
              lng: data.data[0].coordinates.longitude,
            },
          ],
          timezone: data.data[0].timezone,
        });
      }
    } else {
      resetAreaId();
    }
  };

  useEffect(() => {
    if (!areaId) {
      setDefaultAreaId();
    }
  }, [companyId, data, isLoading, isError, areaId, setAreaId, resetAreaId]);

  return (
    <>
      {!isLoading && (
        <>
          {areaId && data ? (
            <>
              <Tooltip
                arrow
                title={`${t('select.area')} ${disabled ? ' (' + t('select.scope_disabled') + ')' : ''}`}
                placement="bottom"
              >
                <Button
                  startIcon={<ViewInArIcon />}
                  aria-expanded={open ? 'true' : undefined}
                  sx={{ color: '#fff', cursor: disabled ? 'not-allowed' : 'pointer' }}
                  onClick={disabled ? undefined : handleClickButton}
                >
                  {areaId.name === 'All' ? t('select.all_areas') : areaId.name}
                </Button>
              </Tooltip>
              <Menu
                id="area-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'area-button',
                  role: 'menu',
                }}
                defaultValue={areaId.id}
              >
                {data.data.length > 1 && (
                  <MenuItem
                    key={'ALL'}
                    value={'All'}
                    onClick={() => handleAreaChange('ALL')}
                    sx={{
                      background: areaId.id === 'ALL' ? '#d9d9d9' : '#fff',
                    }}
                  >
                    {t('select.all_areas')}
                  </MenuItem>
                )}
                {data.data.map((item: { area_id: string; name: string }) => {
                  return (
                    <MenuItem
                      key={item.area_id}
                      value={item.area_id}
                      onClick={() => handleAreaChange(item.area_id)}
                      sx={{
                        background: item.area_id === areaId.id ? '#d9d9d9' : '#fff',
                      }}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ) : (
            <Box>
              <Link to="/areas/create">
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 500, color: '#fff', display: 'flex', alignItems: 'center' }}
                  component="span"
                >
                  <WarningAmberIcon sx={{ mr: '4px' }} />
                  {t('pages.groups.no_area')}
                </Typography>
              </Link>
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default AreaSelect;
